import { useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useSearchParams } from 'react-router'
import { Button, Icon, SpinnerLoader, Typography } from 'shared/ui'
import { OauthPageStore } from 'pages/auth/pages/oauth/store/oauthPageStore'
import { ProviderOauthPage, useOauthPageContext } from 'pages/auth/pages/oauth/context/oauthContext'
import styles from './styles.module.scss'

const OauthPageContent = observer(() => {
  const store = useOauthPageContext()

  return (
    <div className={styles.wrap}>
      <div className={styles.logo}>
        <Icon icon={'salesmessageFull'} fontSize={146} ariaLabel={'SalesmessageLogo'} />
      </div>

      <div className={styles.content}>
        {store.loading ? (
          <SpinnerLoader ariaLabel={'OauthPage'} />
        ) : (
          <>
            <div className={styles.iconCircle}>
              <div className={styles.iconBox}>
                <Icon
                  icon={'info'}
                  fontSize={20}
                  color={'var(--content-primary-primary-inverted)'}
                  ariaLabel={'OauthInfo'}
                />
              </div>
            </div>

            <Typography className={styles.title} variant={'heading-lg'} ariaLabel={'OauthTitle'}>
              Access request
            </Typography>

            <Typography
              className={styles.description}
              variant={'body-lg-regular'}
              color={'var(--content-primary-secondary)'}
              ariaLabel={'OauthDescription'}
            >
              This app would like to access your account
            </Typography>

            {store.error && (
              <Typography
                className={styles.error}
                variant={'body-md-regular'}
                color={'var(--content-negative-primary)'}
                ariaLabel={'OauthError'}
              >
                {store.error}
              </Typography>
            )}

            {store.isReady && (
              <div className={styles.panel}>
                <Typography
                  className={styles.panelTitle}
                  variant={'body-md-semibold'}
                  color={'var(--light-blue-100)'}
                  ariaLabel={'OauthPermissionsTitle'}
                >
                  Permissions requested:
                </Typography>

                <ul className={styles.scopeList}>
                  {store.scopes.map((scope) => (
                    <li key={scope.id} className={styles.scopeItem}>
                      <Typography
                        variant={'body-md-regular'}
                        color={'var(--light-blue-100)'}
                        ariaLabel={'OauthScope'}
                      >
                        {scope.description}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.actionsWrap}>
              {store.isReady && (
                <Button
                  text={'Allow access'}
                  onClick={store.authorize}
                  loading={store.submitting}
                  disabled={store.submitting}
                  aria-label={'OauthAllowAccess'}
                  className={styles.action}
                />
              )}
              <Button
                typeBtn={'stroke'}
                text={'Cancel'}
                onClick={store.cancel}
                disabled={store.submitting}
                aria-label={'OauthCancel'}
                className={styles.action}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
})

export const OauthPage = () => {
  const [searchParams] = useSearchParams()
  const store = useMemo(() => new OauthPageStore(searchParams), [searchParams])

  useEffect(() => {
    store.init()

    return () => store.dispose()
  }, [store])

  return (
    <ProviderOauthPage value={store}>
      <OauthPageContent />
    </ProviderOauthPage>
  )
}

export default OauthPage
